import type { AccountController, RpcClient } from "@hq/client";
import { ClientProvider, type ClientContextValue } from "@hq/client-react";
import {
  CommandPaletteProvider,
  createActionRegistry,
  type ActionContext,
} from "@hq/command-palette";
import { Toaster, TooltipProvider } from "@z/ui";
import { RouterProvider, type RouterHistory } from "@tanstack/react-router";
import { StrictMode, useState } from "react";
import { AppConfigContext, type AppConfig } from "./AppConfig";
import { AppShortcutPreferences } from "./actions/ShortcutPreferences";
import { QuickCaptureContext } from "./actions/QuickCaptureContext";
import { TemplatePickerContext } from "./actions/TemplatePickerContext";
import { createAppRouter } from "./routes/router";
import { AuthGate } from "./auth/AuthGate";
import { QuickCapture } from "./shell/QuickCapture";

export type AppProps = {
  client: ClientContextValue;
  /** Local store boot time, shown in Settings. */
  bootMs?: number;
  /** Whether a sync server is configured (M2). Requires sign-in before the workspace is mounted. */
  hasServer?: boolean;
  account?: AccountController;
  rpc?: RpcClient;
  /** Wipes the local database. Omitted on platforms that cannot. */
  onResetLocalData?: () => Promise<void>;
  /** Leaves the app for a gateway's sign-in page when one blocks the server. Omitted where none can. */
  onReauthenticate?: () => Promise<void>;
  /** Called once the workspace has rendered. The smoke waits on this. */
  onReady?: () => void;
  /** Test hook: a memory history instead of the browser's. */
  history?: RouterHistory;
};

/**
 * The web application, consumed unchanged by `apps/web` and later `apps/desktop`. Wires the
 * feature packages together; this is the only place they meet.
 */
export function App(props: AppProps) {
  const [registry] = useState(() => createActionRegistry());
  const [router] = useState(() => createAppRouter(props.history));
  const [captureOpen, setCaptureOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const config: AppConfig = {
    hasServer: props.hasServer ?? false,
    ...(props.account ? { account: props.account } : {}),
    ...(props.rpc ? { rpc: props.rpc } : {}),
    ...(props.bootMs !== undefined ? { bootMs: props.bootMs } : {}),
    ...(props.onResetLocalData ? { onResetLocalData: props.onResetLocalData } : {}),
    ...(props.onReauthenticate ? { onReauthenticate: props.onReauthenticate } : {}),
    ...(props.onReady ? { onReady: props.onReady } : {}),
  };
  const getContext = (): ActionContext => ({ route: location.pathname });

  return (
    <StrictMode>
      <ClientProvider value={props.client}>
        <AppConfigContext.Provider value={config}>
          <TooltipProvider>
            <AuthGate>
              <AppShortcutPreferences>
                <QuickCaptureContext.Provider
                  value={{
                    open: () => setCaptureOpen(true),
                    close: () => setCaptureOpen(false),
                    isOpen: captureOpen,
                  }}
                >
                  <TemplatePickerContext.Provider
                    value={{
                      open: () => setTemplatesOpen(true),
                      close: () => setTemplatesOpen(false),
                      isOpen: templatesOpen,
                    }}
                  >
                    <CommandPaletteProvider registry={registry} getContext={getContext}>
                      <RouterProvider router={router} />
                      <QuickCapture open={captureOpen} onOpenChange={setCaptureOpen} />
                    </CommandPaletteProvider>
                  </TemplatePickerContext.Provider>
                </QuickCaptureContext.Provider>
                <Toaster />
              </AppShortcutPreferences>
            </AuthGate>
          </TooltipProvider>
        </AppConfigContext.Provider>
      </ClientProvider>
    </StrictMode>
  );
}
